import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import InvoicePrintModal from '../components/InvoicePrintModal';

const PaymentReceipts = () => {
  const { selectedFirmId, firms, company } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('customer');

  // Master lists
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Data lists
  const [customerPayments, setCustomerPayments] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [employeePayments, setEmployeePayments] = useState([]);
  const [dailyCollection, setDailyCollection] = useState(null);

  // Forms
  const [PayDate, setPayDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [customerId, setCustomerId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [PaidAmt, setPaidAmt] = useState('');
  const [Remark, setRemark] = useState('');
  const [paymode, setPaymode] = useState('Cash');

  // Ledger summary
  const [customerLedger, setCustomerLedger] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Print & WhatsApp Modal State
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [printData, setPrintData] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadMasters();
    loadAllPayments();
  }, [selectedFirmId]);


  useEffect(() => {
    if (customerId) {
      fetchCustomerLedger(customerId);
    }
  }, [customerId]);

  const loadMasters = async () => {
    try {
      const [cRes, vRes, eRes] = await Promise.all([
        api.get('/masters/customers'),
        api.get('/masters/vendors'),
        api.get(`/masters/employees?firmId=${selectedFirmId}`)
      ]);
      setCustomers(cRes.data);
      setVendors(vRes.data);
      setEmployees(eRes.data);
    } catch (err) {
      console.error('Error loading payment masters', err);
    }
  };

  const loadAllPayments = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const [cpRes, vpRes, epRes, dcRes] = await Promise.all([
        api.get(`/payments/customers?firmId=${selectedFirmId}`),
        api.get(`/payments/vendors?firmId=${selectedFirmId}`),
        api.get(`/payments/employees?firmId=${selectedFirmId}`),
        api.get(`/payments/daily-collection?date=${today}`)
      ]);

      setCustomerPayments(cpRes.data);
      setVendorPayments(vpRes.data);
      setEmployeePayments(epRes.data);
      setDailyCollection(dcRes.data);
    } catch (err) {
      console.error('Error loading payments', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerLedger = async (cid) => {
    try {
      const res = await api.get(`/payments/customers/ledger/${cid}`);
      setCustomerLedger(res.data);
    } catch (err) {
      console.error('Error loading ledger', err);
    }
  };

  // HANDLERS
  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    const amt = parseFloat(PaidAmt) || 0;
    const currentCust = customers.find(c => String(c.Id) === String(customerId));
    const currentFirm = firms.find(f => String(f.Id) === String(selectedFirmId));

    try {
      const res = await api.post('/payments/customers', {
        PayDate,
        customerId,
        PaidAmt: amt,
        Remark,
        paymode,
        FirmId: selectedFirmId
      });
      setSuccess('Customer payment receipt saved!');
      loadAllPayments();
      if (customerId) fetchCustomerLedger(customerId);

      // Open print/whatsapp modal for customer receipt
      setPrintData({
        id: res.data.id || '1',
        saleNo: res.data.id || 'REC',
        PayDate: PayDate,
        customerName: currentCust ? currentCust.Name : 'Customer',
        customerAddress: currentCust ? currentCust.Address : '',
        customerMobile: currentCust ? currentCust.MobileNo : '',
        firmName: currentFirm?.firmName || company?.Comapny_Name || 'Hari Ganga Fish Syndicate',
        firmAddress: currentFirm?.Address || company?.Comapny_Address || 'Sakhar Koliwada, Alibag, Raigad',
        firmContact: currentFirm?.ContactNo || company?.Company_Mobile || '9881914831',
        items: [
          {
            productName: `Payment Received (${paymode}) - ${Remark || 'On Account'}`,
            net_qty: 1,
            Rate: amt,
            total: amt
          }
        ],
        PaidAmt: amt,
        GrandTotal: amt,
        balance: customerLedger ? (customerLedger.balance - amt) : 0
      });
      setIsPrintOpen(true);

      setPaidAmt(''); setRemark('');
    } catch (err) {
      setError('Failed to record customer payment');
    }
  };

  const handleOpenReceiptPrint = (cp) => {
    const currentCust = customers.find(c => String(c.Id) === String(cp.customerId)) || { Name: cp.customerName, MobileNo: cp.customerMobile, Address: cp.customerAddress };
    const currentFirm = firms.find(f => String(f.Id) === String(cp.FirmId || selectedFirmId));

    setPrintData({
      id: cp.id,
      saleNo: cp.id,
      PayDate: cp.PayDate,
      customerName: currentCust?.Name || cp.customerName,
      customerAddress: currentCust?.Address || cp.customerAddress || '',
      customerMobile: currentCust?.MobileNo || cp.customerMobile || '',
      firmName: currentFirm?.firmName || company?.Comapny_Name || 'Hari Ganga Fish Syndicate',
      firmAddress: currentFirm?.Address || company?.Comapny_Address || 'Sakhar Koliwada, Alibag, Raigad',
      firmContact: currentFirm?.ContactNo || company?.Company_Mobile || '9881914831',
      items: [
        {
          productName: `Payment Receipt - ${cp.Remark || 'Fish Account Payment'}`,
          net_qty: 1,
          Rate: cp.PaidAmt,
          total: cp.PaidAmt
        }
      ],
      PaidAmt: cp.PaidAmt,
      GrandTotal: cp.PaidAmt,
      balance: 0
    });
    setIsPrintOpen(true);
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await api.post('/payments/vendors', {
        payDate: PayDate,
        vendorId,
        PaidAmt: parseFloat(PaidAmt) || 0,
        Remark,
        paymode,
        FirmId: selectedFirmId
      });
      setSuccess('Vendor payment voucher saved!');
      setPaidAmt(''); setRemark('');
      loadAllPayments();
    } catch (err) {
      setError('Failed to record vendor payment');
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await api.post('/payments/employees', {
        payDate: PayDate,
        EmployeeId: employeeId,
        PaidAmt: parseFloat(PaidAmt) || 0,
        Remark,
        firmid: selectedFirmId
      });
      setSuccess('Employee salary / payout recorded!');
      setPaidAmt(''); setRemark('');
      loadAllPayments();
    } catch (err) {
      setError('Failed to record employee payment');
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h3 className="fw-bold mb-1 text-primary">
            <i className="bi bi-cash-stack me-2"></i>Payments & Collections (पेमेंट व जमा-खर्च)
          </h3>
          <p className="text-muted mb-0">Record customer receipts, vendor vouchers, employee salaries, and view daily collection</p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {success && <div className="alert alert-success py-2">{success}</div>}

      {/* TABS HEADER */}
      <ul className="nav nav-tabs mb-4 fw-bold">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'customer' ? 'active text-primary' : ''}`}
            onClick={() => setActiveTab('customer')}
          >
            <i className="bi bi-wallet2 me-2"></i>Customer Receipts (ग्राहकांचे जमा)
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'vendor' ? 'active text-primary' : ''}`}
            onClick={() => setActiveTab('vendor')}
          >
            <i className="bi bi-card-checklist me-2"></i>Vendor Vouchers (विक्रेता पेमेंट)
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'employee' ? 'active text-primary' : ''}`}
            onClick={() => setActiveTab('employee')}
          >
            <i className="bi bi-person-badge me-2"></i>Employee Payouts (कामगार पगार)
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'daily' ? 'active text-success' : ''}`}
            onClick={() => setActiveTab('daily')}
          >
            <i className="bi bi-calendar-check me-2"></i>View Daily Collection
          </button>
        </li>
      </ul>

      {/* TAB 1: CUSTOMER RECEIPTS */}
      {activeTab === 'customer' && (
        <div className="row g-4">
          <div className="col-12 col-md-5">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white fw-bold">
                New Customer Payment Receipt
              </div>
              <div className="card-body">
                <form onSubmit={handleCustomerSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Payment Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={PayDate}
                      onChange={(e) => setPayDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Customer (व्यापारी नांव)</label>
                    <select
                      className="form-select fw-semibold"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    >
                      <option value="">-- Select Customer --</option>
                      {customers.map(c => (
                        <option key={c.Id} value={c.Id}>{c.Name}</option>
                      ))}
                    </select>
                  </div>

                  {/* LIVE LEDGER PREVIEW */}
                  {customerLedger && (
                    <div className="p-3 bg-light rounded border mb-3">
                      <div className="d-flex justify-content-between small mb-1">
                        <span>Total Purchases / Sales:</span>
                        <span className="fw-bold">₹{customerLedger.totalSales.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="d-flex justify-content-between small mb-1 text-success">
                        <span>Total Paid Till Date:</span>
                        <span className="fw-bold">₹{customerLedger.totalPaid.toLocaleString('en-IN')}</span>
                      </div>
                      <hr className="my-1" />
                      <div className="d-flex justify-content-between fw-bold text-danger">
                        <span>Current Due Balance:</span>
                        <span>₹{customerLedger.balance.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fw-semibold text-success">Amount Received (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-lg fw-bold text-success"
                      placeholder="0.00"
                      value={PaidAmt}
                      onChange={(e) => setPaidAmt(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Payment Mode</label>
                    <select className="form-select" value={paymode} onChange={(e) => setPaymode(e.target.value)}>
                      <option value="Cash">Cash</option>
                      <option value="UPI / GPay">UPI / GPay / PhonePe</option>
                      <option value="Bank Transfer / NEFT">Bank Transfer / NEFT</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-semibold">Remark / Note</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Optional note e.g. Cash payment"
                      value={Remark}
                      onChange={(e) => setRemark(e.target.value)}
                    />
                  </div>

                  <button type="submit" className="btn btn-primary w-100 fw-bold shadow-sm">
                    <i className="bi bi-check-circle me-1"></i> Save Receipt
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-7">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="fw-bold mb-0 text-dark">Customer Payment History</h5>
              </div>
              <div className="table-responsive" style={{ maxHeight: '550px' }}>
                <table className="table table-hover align-middle mb-0 small">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Date</th>
                      <th>Customer Name</th>
                      <th>Remark</th>
                      <th className="text-end">Amount Paid</th>
                      <th className="text-end">पावती (Receipt)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          <span className="spinner-border spinner-border-sm me-2"></span> Loading payments...
                        </td>
                      </tr>
                    ) : customerPayments.filter(p => p.PaidAmt > 0).length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">No receipts found.</td>
                      </tr>
                    ) : (
                      customerPayments.filter(p => p.PaidAmt > 0).map((cp) => (
                      <tr key={cp.id}>
                        <td>{cp.PayDate}</td>
                        <td className="fw-semibold text-primary">
                          <div>{cp.customerName}</div>
                          {cp.customerMobile && (
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                              <i className="bi bi-phone me-1"></i>{cp.customerMobile}
                            </div>
                          )}
                        </td>
                        <td>{cp.Remark || '-'}</td>
                        <td className="text-end fw-bold text-success">
                          ₹{(cp.PaidAmt || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              type="button"
                              className="btn btn-outline-dark py-0 px-2"
                              title="पावती प्रिंट करा (Print Receipt)"
                              onClick={() => handleOpenReceiptPrint(cp)}
                            >
                              <i className="bi bi-printer"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-success py-0 px-2"
                              title="व्हॉट्सॲपवर पाठवा (Send WhatsApp Receipt)"
                              onClick={() => handleOpenReceiptPrint(cp)}
                            >
                              <i className="bi bi-whatsapp"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: VENDOR VOUCHERS */}
      {activeTab === 'vendor' && (
        <div className="row g-4">
          <div className="col-12 col-md-5">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white fw-bold">
                New Vendor Payment Voucher
              </div>
              <div className="card-body">
                <form onSubmit={handleVendorSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Payment Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={PayDate}
                      onChange={(e) => setPayDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Supplier / Vendor (विक्रेता)</label>
                    <select
                      className="form-select fw-semibold"
                      value={vendorId}
                      onChange={(e) => setVendorId(e.target.value)}
                      required
                    >
                      <option value="">-- Select Supplier --</option>
                      {vendors.map(v => (
                        <option key={v.Id} value={v.Id}>{v.Name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-danger">Amount Paid (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-lg fw-bold text-danger"
                      placeholder="0.00"
                      value={PaidAmt}
                      onChange={(e) => setPaidAmt(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Payment Mode</label>
                    <select className="form-select" value={paymode} onChange={(e) => setPaymode(e.target.value)}>
                      <option value="Cash">Cash</option>
                      <option value="UPI / GPay">UPI / GPay</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Remark / Description</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Diesel payment"
                      value={Remark}
                      onChange={(e) => setRemark(e.target.value)}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary w-100 fw-bold shadow-sm">
                    <i className="bi bi-check-circle me-1"></i> Save Voucher
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-7">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="fw-bold mb-0 text-dark">Vendor Payouts History</h5>
              </div>
              <div className="table-responsive" style={{ maxHeight: '550px' }}>
                <table className="table table-hover align-middle mb-0 small">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Date</th>
                      <th>Supplier Name</th>
                      <th>Remark</th>
                      <th className="text-end">Amount Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorPayments.filter(vp => vp.PaidAmt > 0).map((vp) => (
                      <tr key={vp.Id}>
                        <td>{vp.payDate}</td>
                        <td className="fw-semibold text-primary">{vp.vendorName}</td>
                        <td>{vp.Remark || '-'}</td>
                        <td className="text-end fw-bold text-danger">
                          ₹{(vp.PaidAmt || 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: EMPLOYEE PAYOUTS */}
      {activeTab === 'employee' && (
        <div className="row g-4">
          <div className="col-12 col-md-5">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white fw-bold">
                Employee Salary / Commission Disbursal
              </div>
              <div className="card-body">
                <form onSubmit={handleEmployeeSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Payment Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={PayDate}
                      onChange={(e) => setPayDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Crew Member (कामगार)</label>
                    <select
                      className="form-select fw-semibold"
                      value={employeeId}
                      onChange={(e) => setEmployeeId(e.target.value)}
                      required
                    >
                      <option value="">-- Select Crew Member --</option>
                      {employees.map(e => (
                        <option key={e.id} value={e.id}>{e.Emp_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-primary">Amount Disbursed (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-lg fw-bold text-primary"
                      placeholder="0.00"
                      value={PaidAmt}
                      onChange={(e) => setPaidAmt(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Remark</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Salary advance / Trip Payout"
                      value={Remark}
                      onChange={(e) => setRemark(e.target.value)}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary w-100 fw-bold shadow-sm">
                    <i className="bi bi-check-circle me-1"></i> Disburse Payout
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-7">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="fw-bold mb-0 text-dark">Crew Payout History</h5>
              </div>
              <div className="table-responsive" style={{ maxHeight: '550px' }}>
                <table className="table table-hover align-middle mb-0 small">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Date</th>
                      <th>Crew Name</th>
                      <th>Remark</th>
                      <th className="text-end">Paid Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeePayments.filter(ep => ep.PaidAmt > 0).map((ep) => (
                      <tr key={ep.id}>
                        <td>{ep.payDate}</td>
                        <td className="fw-semibold text-primary">{ep.Emp_name}</td>
                        <td>{ep.Remark || '-'}</td>
                        <td className="text-end fw-bold text-primary">
                          ₹{(ep.PaidAmt || 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: DAILY COLLECTION */}
      {activeTab === 'daily' && dailyCollection && (
        <div className="card border-0 shadow-sm p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="fw-bold text-success mb-0">
              <i className="bi bi-calendar-check me-2"></i>Daily Collection & Cash Flow ({dailyCollection.date})
            </h4>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-12 col-md-4">
              <div className="card bg-success text-white border-0 shadow-sm">
                <div className="card-body">
                  <span className="text-uppercase small">Total Customer Receipts</span>
                  <h2 className="fw-bold mt-2">₹{dailyCollection.customerReceiptsTotal.toLocaleString('en-IN')}</h2>
                  <span className="small text-white-50">Cash / Online Inflow</span>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="card bg-danger text-white border-0 shadow-sm">
                <div className="card-body">
                  <span className="text-uppercase small">Total Outflows (Vendor + Salary)</span>
                  <h2 className="fw-bold mt-2">
                    ₹{(dailyCollection.vendorPaymentsTotal + dailyCollection.employeePayoutsTotal + dailyCollection.generalExpensesTotal).toLocaleString('en-IN')}
                  </h2>
                  <span className="small text-white-50">Supplier & Crew Payouts</span>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="card bg-primary text-white border-0 shadow-sm">
                <div className="card-body">
                  <span className="text-uppercase small">Net Cash Flow Today</span>
                  <h2 className="fw-bold mt-2">₹{dailyCollection.netCashFlow.toLocaleString('en-IN')}</h2>
                  <span className="small text-white-50">Net Remaining Balance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT RECEIPT ONLINE PRINT & WHATSAPP MODAL */}
      <InvoicePrintModal
        isOpen={isPrintOpen}
        onClose={() => setIsPrintOpen(false)}
        title="पेमेंट पावती / Payment Receipt"
        invoiceType="receipt"
        data={printData}
      />
    </div>
  );
};

export default PaymentReceipts;

